import { Nullable } from 'src/core/common/domain/types/types';

export enum WorkTypeKind {
  PLANNED = 'planned',
  IMMEDIATE = 'immediate',
}

export type WorkTypePrimitives = {
  id: string;
  name: string;
  // Optional: a task's own payload carries only id and name, and records stored before the master
  // data contract grew these fields do not have them either.
  position?: Nullable<number>;
  type?: Nullable<string>;
};

export class WorkType {
  // Work types the backend does not order sort after the ordered ones, keeping the order it sent
  // them in. Same convention as Task.sortPriority for tasks without a priority.
  static readonly LAST_POSITION: number = Number.MAX_SAFE_INTEGER;

  /*
   * Work type ids that meant "planned" before the contract carried an explicit type. Deprecated:
   * delete this list, and the branch of kindOf that reads it, once every backend sends
   * filters.work_types.type. WorkTypePrimitives.type then becomes required.
   */
  private static readonly LEGACY_PLANNED_IDS: Array<string> = ['planned', 'PL', 'FL'];

  // Private so no caller can build a work type while skipping kindOf, which is where the
  // backward-compatible inference lives.
  private constructor(
    public readonly id: string,
    public readonly name: string,
    public readonly position: number,
    public readonly type: WorkTypeKind
  ) {}

  static fromPrimitives(primitives: WorkTypePrimitives): WorkType {
    return new this(primitives.id, primitives.name, primitives.position ?? this.LAST_POSITION, this.kindOf(primitives));
  }

  static planned(id: string, name: string, position: number = WorkType.LAST_POSITION): WorkType {
    return new this(id, name, position, WorkTypeKind.PLANNED);
  }

  static immediate(id: string, name: string, position: number = WorkType.LAST_POSITION): WorkType {
    return new this(id, name, position, WorkTypeKind.IMMEDIATE);
  }

  static sortedByPosition(workTypes: Array<WorkType>): Array<WorkType> {
    // Array.prototype.sort is stable, so work types sharing a position keep the order they came in.
    const sorted = [...workTypes].sort((one, other) => one.position - other.position);
    return sorted;
  }

  isPlanned(): boolean {
    return this.type === WorkTypeKind.PLANNED;
  }

  // Not "type === IMMEDIATE": planned and immediate must partition the set, because the task card
  // renders each field under an isPlanned/isImmediate pair and a work type answering false to both
  // would blank them.
  isImmediate(): boolean {
    return !this.isPlanned();
  }

  toPrimitives(): WorkTypePrimitives {
    return {
      id: this.id,
      name: this.name,
      position: this.position,
      type: this.type,
    };
  }

  private static kindOf(primitives: WorkTypePrimitives): WorkTypeKind {
    if (primitives.type) {
      /*
       * An unrecognised kind is immediate rather than an error: HttpMasterDataRepository turns any
       * throw into a user-facing MasterDataBadFormatException, so a kind added server-side must not
       * be able to take the app down.
       */
      return primitives.type === WorkTypeKind.PLANNED ? WorkTypeKind.PLANNED : WorkTypeKind.IMMEDIATE;
    }

    return this.LEGACY_PLANNED_IDS.includes(primitives.id) ? WorkTypeKind.PLANNED : WorkTypeKind.IMMEDIATE;
  }
}
